import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiActivity, FiTarget, FiTrendingUp, FiTrendingDown, FiCheck } from 'react-icons/fi';

const BMICalculator = () => {
  const [formData, setFormData] = useState({
    height: '',
    weight: '',
    unit: 'metric' // metric or imperial
  });
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Prevent negative numbers (contain minus sign or are less than zero)
    if (value !== '' && (parseFloat(value) < 0 || value.toString().includes('-'))) {
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.height || formData.height <= 0) {
      newErrors.height = 'Please enter a valid height';
    }
    
    if (!formData.weight || formData.weight <= 0) {
      newErrors.weight = 'Please enter a valid weight';
    }

    if (formData.unit === 'metric') {
      if (formData.height > 300) newErrors.height = 'Height must be in centimeters (e.g., 170)';
      if (formData.weight > 500) newErrors.weight = 'Please enter a realistic weight';
    } else {
      if (formData.height > 10) newErrors.height = 'Height must be in feet (e.g., 5.8)';
      if (formData.weight > 1000) newErrors.weight = 'Please enter a realistic weight';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateBMI = () => {
    if (!validateForm()) return;

    let height = parseFloat(formData.height);
    let weight = parseFloat(formData.weight);

    // Convert to metric if needed
    if (formData.unit === 'imperial') {
      height = height * 30.48; // feet to cm
      weight = weight * 0.453592; // pounds to kg
    } else {
      height = height; // already in cm
    }

    // Convert height to meters
    const heightInMeters = height / 100;
    const bmi = weight / (heightInMeters * heightInMeters);

    let category, color, icon, recommendations;

    if (bmi < 18.5) {
      category = 'Underweight';
      color = 'text-blue-600';
      icon = <FiTrendingUp className="w-5 h-5" />;
      recommendations = [
        'Increase calorie intake with nutritious foods',
        'Add protein-rich foods to your diet',
        'Consider strength training exercises',
        'Consult with a nutritionist for a weight gain plan'
      ];
    } else if (bmi >= 18.5 && bmi < 25) {
      category = 'Normal weight';
      color = 'text-green-600';
      icon = <FiCheck className="w-5 h-5" />;
      recommendations = [
        'Maintain your current healthy lifestyle',
        'Continue regular physical activity',
        'Keep eating a balanced diet',
        'Monitor your weight regularly'
      ];
    } else if (bmi >= 25 && bmi < 30) {
      category = 'Overweight';
      color = 'text-yellow-600';
      icon = <FiTarget className="w-5 h-5" />;
      recommendations = [
        'Focus on portion control',
        'Increase physical activity',
        'Choose healthier food options',
        'Consider our weight loss programs'
      ];
    } else {
      category = 'Obese';
      color = 'text-red-600';
      icon = <FiTrendingDown className="w-5 h-5" />;
      recommendations = [
        'Consult with healthcare professionals',
        'Start with low-impact exercises',
        'Focus on sustainable dietary changes',
        'Consider our personalized weight loss plans'
      ];
    }

    setResult({
      bmi: bmi.toFixed(1),
      category,
      color,
      icon,
      recommendations
    });
  };

  const resetCalculator = () => {
    setFormData({
      height: '',
      weight: '',
      unit: 'metric'
    });
    setResult(null);
    setErrors({});
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="bg-gradient-to-r from-primary-500 to-primary-600 p-6 text-white">
        <div className="flex items-center space-x-3 mb-2">
          <FiActivity className="w-6 h-6" />
          <h3 className="text-xl font-bold">BMI Calculator</h3>
        </div>
        <p className="text-primary-100">
          Calculate your Body Mass Index and get personalized recommendations
        </p>
      </div>

      <div className="p-6">
        {/* Unit Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Choose Unit System
          </label>
          <div className="flex space-x-4">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, unit: 'metric', height: '', weight: '' }))}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                formData.unit === 'metric'
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Metric (cm/kg)
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, unit: 'imperial', height: '', weight: '' }))}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                formData.unit === 'imperial'
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Imperial (ft/lbs)
            </button>
          </div>
        </div>

        {/* Input Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Height Input */}
          <div>
            <label htmlFor="height" className="block text-sm font-medium text-gray-700 mb-1">
              Height {formData.unit === 'metric' ? '(cm)' : '(feet)'}
            </label>
            <input
              type="number"
              id="height"
              name="height"
              value={formData.height}
              onChange={handleInputChange}
              placeholder={formData.unit === 'metric' ? 'e.g., 170' : 'e.g., 5.8'}
              className={`input-field ${errors.height ? 'border-red-500' : ''}`}
              step="0.1"
            />
            {errors.height && (
              <p className="text-red-500 text-sm mt-1">{errors.height}</p>
            )}
          </div>

          {/* Weight Input */}
          <div>
            <label htmlFor="weight" className="block text-sm font-medium text-gray-700 mb-1">
              Weight {formData.unit === 'metric' ? '(kg)' : '(lbs)'}
            </label>
            <input
              type="number"
              id="weight"
              name="weight"
              value={formData.weight}
              onChange={handleInputChange}
              placeholder={formData.unit === 'metric' ? 'e.g., 70' : 'e.g., 154'}
              className={`input-field ${errors.weight ? 'border-red-500' : ''}`}
              step="0.1"
            />
            {errors.weight && (
              <p className="text-red-500 text-sm mt-1">{errors.weight}</p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3 mb-6">
          <button
            onClick={calculateBMI}
            className="btn-primary flex-1"
          >
            Calculate BMI
          </button>
          {result && (
            <button
              onClick={resetCalculator}
              className="btn-secondary"
            >
              Reset
            </button>
          )}
        </div>

        {/* Results */}
        <AnimatePresence>
          {result && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-gray-50 rounded-lg p-6"
            >
              <div className="text-center mb-6">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <span className={result.color}>{result.icon}</span>
                  <h4 className="text-2xl font-bold text-gray-800">
                    BMI: {result.bmi}
                  </h4>
                </div>
                <p className={`text-lg font-semibold ${result.color}`}>
                  {result.category}
                </p>
              </div>

              {/* Recommendations */}
              <div>
                <h5 className="font-semibold text-gray-800 mb-3">
                  Recommendations for you:
                </h5>
                <ul className="space-y-2">
                  {result.recommendations.map((rec, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      className="flex items-start space-x-2"
                    >
                      <FiCheck className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-600">{rec}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>

              {/* CTA */}
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600 mb-3">
                  Want personalized nutrition and fitness guidance?
                </p>
                <Link to="/contact#consultation" className="btn-primary inline-block">
                  Enroll Now
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default BMICalculator;